/**
 * VaultUploader: Drag-and-drop file upload zone with per-file progress bars
 */
import { useRef, useState, useCallback } from 'react';
import { Upload, X, CheckCircle2, AlertCircle, Loader2, FileUp } from 'lucide-react';
import type { UploadProgress } from '../../types/vault.types';
import { formatFileSize } from '../../types/vault.types';

interface VaultUploaderProps {
  uploading: boolean;
  uploadProgress: UploadProgress[];
  onUpload: (files: File[]) => Promise<void>;
  onCancel: () => void;
}

export function VaultUploader({ uploading, uploadProgress, onUpload, onCancel }: VaultUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      await onUpload(droppedFiles);
    }
  }, [onUpload]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 0) {
      await onUpload(selected);
    }
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onUpload]);

  const getStatusIcon = (status: UploadProgress['status']) => {
    switch (status) {
      case 'complete': return <CheckCircle2 size={16} className="vault-upload-icon success" />;
      case 'error': return <AlertCircle size={16} className="vault-upload-icon error" />;
      case 'uploading': return <Loader2 size={16} className="vault-upload-icon uploading" />;
      default: return <FileUp size={16} className="vault-upload-icon pending" />;
    }
  };

  return (
    <>
      {/* Drag overlay */}
      {isDragging && (
        <div className="vault-drag-overlay">
          <div className="vault-drag-overlay-content">
            <Upload size={48} />
            <h3>Drop files to upload</h3>
            <p>Release to start uploading</p>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Upload button trigger */}
      <button
        className="btn btn-primary vault-upload-btn"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
      >
        <Upload size={16} />
        <span>{uploading ? 'Uploading...' : 'Upload'}</span>
      </button>

      {/* Upload progress panel */}
      {uploadProgress.length > 0 && (
        <div className="vault-upload-progress-panel">
          <div className="vault-upload-progress-header">
            <span className="vault-upload-progress-title">
              {uploading ? (
                <><Loader2 size={14} className="spin" /> Uploading files...</>
              ) : (
                <><CheckCircle2 size={14} /> Upload complete</>
              )}
            </span>
            {uploading && (
              <button className="vault-upload-cancel" onClick={onCancel}>
                <X size={14} /> Cancel
              </button>
            )}
          </div>

          <div className="vault-upload-progress-list">
            {uploadProgress.map((item, i) => (
              <div key={i} className={`vault-upload-progress-item ${item.status}`}>
                <div className="vault-upload-progress-info">
                  {getStatusIcon(item.status)}
                  <span className="vault-upload-progress-name">{item.fileName}</span>
                  <span className="vault-upload-progress-size">{formatFileSize(item.fileSize)}</span>
                </div>
                <div className="vault-upload-progress-bar-track">
                  <div
                    className="vault-upload-progress-bar-fill"
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
                {item.error && <div className="vault-upload-progress-error">{item.error}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expose drag handlers for parent to attach */}
      <div
        className="vault-drop-zone-wrapper"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{ display: 'contents' }}
      />
    </>
  );
}

/**
 * VaultDropZone: Wraps an area to make it a drop target
 */
interface VaultDropZoneProps {
  children: React.ReactNode;
  onDrop: (files: File[]) => Promise<void>;
  uploading: boolean;
}

export function VaultDropZone({ children, onDrop, uploading }: VaultDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  // Detect if drag is from OS (external files) vs internal vault items
  const isExternalFileDrag = (e: React.DragEvent): boolean => {
    // Internal vault drags set 'text/plain' with 'file:xxx' or 'folder:xxx'
    // External OS drags have 'Files' type with actual file items
    const types = Array.from(e.dataTransfer.types);
    // If it has 'text/plain' without 'Files', it's internal
    if (types.includes('text/plain') && !types.includes('Files')) return false;
    // If it has 'Files', check if items are actual files (not just text)
    if (types.includes('Files')) {
      const items = Array.from(e.dataTransfer.items || []);
      return items.some(item => item.kind === 'file');
    }
    return false;
  };

  return (
    <div
      className={`vault-drop-zone ${isDragging ? 'dragging' : ''}`}
      onDragEnter={(e) => {
        e.preventDefault();
        dragCounter.current++;
        if (isExternalFileDrag(e)) {
          setIsDragging(true);
        }
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        dragCounter.current--;
        if (dragCounter.current === 0) setIsDragging(false);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={async (e) => {
        // Only handle external file drops; internal moves are handled by folder/tree drop handlers
        if (!isDragging) return;
        e.preventDefault();
        setIsDragging(false);
        dragCounter.current = 0;
        if (!uploading) {
          const dropped = Array.from(e.dataTransfer.files);
          if (dropped.length > 0) await onDrop(dropped);
        }
      }}
    >
      {isDragging && (
        <div className="vault-drag-overlay">
          <div className="vault-drag-overlay-content">
            <Upload size={48} />
            <h3>Drop files to upload</h3>
            <p>Release to start uploading</p>
          </div>
        </div>
      )}
      {children}
    </div>
  );
}
